import { injectable, inject } from 'inversify';
import { WindowOption } from 'openfin/_v2/api/window/windowOption';
import ReactDOM from 'react-dom';

import { Inject } from '../common/Injectables';
import { MonitorModel } from '../model/MonitorModel';
import { WebWindow, WebWindowFactory } from '../model/WebWindow';
import { LayoutableWindow, Point } from '../controller/LayoutableWindow';
import { TOAST_WINDOW_NAME } from '../constants';

import { AsyncInit } from './AsyncInit';
import { Layouter } from './Layouter';
import { Radius } from '@openfin/ui-library';

import { ServiceStore } from '../store/ServiceStore';

const windowOptions: WindowOption = {
  name: TOAST_WINDOW_NAME,
  url: 'toast.html',
  autoShow: false,
  defaultHeight: 800,
  defaultWidth: 312,
  resizable: false,
  shadow: true,
  saveWindowState: false,
  contextMenu: !(process.env.NODE_ENV === 'production'),
  frame: false,
  alwaysOnTop: true,
  showTaskbarIcon: false,
  opacity: 1,
  // Corner rounding should be 4px but the right side of the window doesn't match the left unless set larger than the target size.
  cornerRounding: {
    height: parseInt(Radius.base),
    width: parseInt(Radius.base),
  },
  backgroundColor: '#2F3136',
};

/**
 * Handles the expiry of notifications that have specified an `expires` time. Fires an `ExpireNotification` action when
 * a notification has expired.
 */
@injectable()
export class WindowManager extends AsyncInit {
  private static _measurementWindow: WebWindow;
  private readonly _store: ServiceStore;

  public constructor(
    @inject(Inject.MONITOR_MODEL) private readonly _monitorModel: MonitorModel,
    @inject(Inject.WEB_WINDOW_FACTORY) private readonly _webWindowFactory: WebWindowFactory,
    @inject(Inject.LAYOUTER) private readonly _layouter: Layouter,
    @inject(Inject.STORE) store: ServiceStore
  ) {
    super();
    this._store = store;
  }

  protected async init(): Promise<void> {
    await this._monitorModel.initialized;
    WindowManager._measurementWindow = await this._webWindowFactory.createWebWindow({
      ...windowOptions,
      name: 'measurement',
    });
  }

  private readonly mouseEnterHandler = (): void => {
    LayoutableWindow.onFreezeToggle.emit(true);
  };

  private readonly mouseLeaveHandler = (): void => {
    LayoutableWindow.onFreezeToggle.emit(false);
  };

  public async measurementWindow(): Promise<WebWindow> {
    return WindowManager._measurementWindow;
  }

  public async grab(windowName: string, size: Point): Promise<WebWindow> {
    // Show window offscreen so it can render and then hide it
    const { virtualScreen } = this._monitorModel.monitorInfo;
    /**
     * Figure out where the toast will load from, and properly hide the toast near that location
     * This removes the fading toast streaking across the screen. Introduced after flicker was fixed
     * by removing getBounds in setTransform on LayoutableWindows.
     */
    let showToastPosLeft = 0;
    let showToastPosTop = 0;
    const spacing = 10; // TODO: consdier importing spacing from layouterConfig
    const right = virtualScreen.right - ((windowOptions.defaultWidth ?? 0) + spacing);
    const left = spacing;
    const top = spacing;
    const bottom = virtualScreen.top + (windowOptions.defaultHeight ?? 0) * 3;
    switch (this._store.state.settings.toastPosition) {
      case 'bottom-right':
        showToastPosTop = bottom;
        showToastPosLeft = right;
        break;
      case 'bottom-left':
        showToastPosTop = bottom;
        showToastPosLeft = left;
        break;
      case 'top-right':
        showToastPosTop = top;
        showToastPosLeft = right;
        break;
      case 'top-left':
        showToastPosTop = top;
        showToastPosLeft = left;
        break;
    }

    const window = this._webWindowFactory.createWebWindow({
      ...windowOptions,
      name: windowName,
      defaultHeight: size.y,
      defaultWidth: size.x,
      defaultLeft: showToastPosLeft,
      defaultTop: showToastPosTop,
    });

    const webWindow = await window;
    webWindow.onMouseEnter.add(this.mouseEnterHandler);
    webWindow.onMouseLeave.add(this.mouseLeaveHandler);

    await webWindow.updateOptions({ opacity: 0 });

    return window;
  }

  public async release(window?: Promise<WebWindow>): Promise<void> {
    if (!window) {
      return;
    }

    const { document } = await window;
    setImmediate(() => {
      const elem = document.getElementById('react-app');

      if (elem) {
        ReactDOM.unmountComponentAtNode(elem);
      }
    });

    const webWindow = await window.catch(() => null);
    if (webWindow) {
      webWindow.onMouseEnter.remove(this.mouseEnterHandler);
      webWindow.onMouseLeave.remove(this.mouseLeaveHandler);
      webWindow.close();
    }
  }

  /**
   * Emit to the Layout engine that a window size has change and it needs to perform a relayout of the windows.
   */
  public relayout(): void {
    this._layouter.onLayoutRequired.emit();
  }
}
